import {
  AgentComponentMode,
  type AgentComponentType,
  MAX_AGENT_COMPONENT_TENANT_DESCRIPTION_LENGTH,
  MAX_AGENT_COMPONENT_TENANT_NAME_LENGTH,
} from '@reach/shared-types/agent-components';
import { EyeOff } from 'lucide-react';
import { COMPONENT_PLACEHOLDERS } from '../agents-copy';
import type { ComponentDraft, ComponentDraftChange } from './component-draft';
import { COMPONENT_PAGE_COPY } from './component-page-copy';
import { FormCard } from './FormCard';
import styles from './TenantDisplayCard.module.css';
import { TextField } from './TextField';

interface TenantDisplayCardProps {
  type: AgentComponentType;
  draft: ComponentDraft;
  onDraftChange: (change: ComponentDraftChange) => void;
}

/** How the component is named and described to tenants in onboarding and settings. */
export const TenantDisplayCard = ({ type, draft, onDraftChange }: TenantDisplayCardProps) => {
  const placeholders = COMPONENT_PLACEHOLDERS[type];
  const componentIsHidden = draft.mode === AgentComponentMode.HIDDEN;
  const hiddenNotice = (
    <>
      {COMPONENT_PAGE_COPY.tenantHiddenNoticeStart}
      <strong>{COMPONENT_PAGE_COPY.modeLegend}</strong>
      {COMPONENT_PAGE_COPY.tenantHiddenNoticeEnd}
    </>
  );

  return (
    <FormCard
      title={COMPONENT_PAGE_COPY.tenantHeading}
      description={COMPONENT_PAGE_COPY.tenantDescription}>
      {componentIsHidden && (
        <p className={styles.hiddenNotice}>
          <EyeOff aria-hidden="true" />
          <span>{hiddenNotice}</span>
        </p>
      )}
      <fieldset className={styles.fields} disabled={componentIsHidden}>
        <TextField
          label={COMPONENT_PAGE_COPY.tenantNameLabel}
          help={COMPONENT_PAGE_COPY.tenantNameHelp}
          placeholder={placeholders.tenantName}
          value={draft.tenantName}
          maxLength={MAX_AGENT_COMPONENT_TENANT_NAME_LENGTH}
          required
          onChange={tenantName => onDraftChange({ tenantName })}
        />
        <TextField
          label={COMPONENT_PAGE_COPY.tenantDescriptionLabel}
          help={COMPONENT_PAGE_COPY.tenantDescriptionHelp}
          placeholder={placeholders.tenantDescription}
          value={draft.tenantDescription}
          maxLength={MAX_AGENT_COMPONENT_TENANT_DESCRIPTION_LENGTH}
          required
          multiline
          showCount
          onChange={tenantDescription => onDraftChange({ tenantDescription })}
        />
      </fieldset>
    </FormCard>
  );
};
